// Users tab — list + filters + pagination + detail drawer + admin actions.
//
// All write actions (enable/disable/reset pwd/toggle super/anonymize) require
// step-up 2FA via the StepUpContext.

import { useState } from "react";
import { SortableHeader } from "@/components/admin/SortableHeader";
import { UsersCsvExportButton } from "@/components/admin/UsersCsvExportButton";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { KebabMenu } from "@/components/ui/KebabMenu";
import { Loading } from "@/components/ui/Loading";
import { confirm } from "@/stores/confirmStore";
import { toast } from "@/stores/toastStore";
import { useStepUp } from "@/context/StepUpContext";
import {
  downloadUserExport,
  useAdminUsers,
  useAdminUserDetail,
  useAnonymizeUser,
  useMarkEmailVerified,
  useResetUserPassword,
  useSetSuperuser,
  useToggleUserActive,
} from "@/hooks/useAdmin";
import { useAuthStore } from "@/stores/authStore";
import type { AdminUserDetail, AdminUserRow } from "@/types/admin";
import { useDict, fmt } from "@/i18n/LocaleProvider";

const LIMIT = 50;
// User / Status / Role / Email-verified / 2FA / Last login / (action)
const GRID = "2.5fr 90px 90px 100px 90px 110px 80px";

function formatErr(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export function AdminUsersTab() {
  const dd = useDict();
  const t = dd.app.adminUsers;
  const roleMap = dd.app.members.roles;
  const { withStepUp } = useStepUp();
  const currentUser = useAuthStore((s) => s.user);

  const [q, setQ] = useState("");
  const [filterActive, setFilterActive] = useState("");
  const [filterSuper, setFilterSuper] = useState("");
  const [filter2FA, setFilter2FA] = useState("");
  const [offset, setOffset] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [order, setOrder] = useState<"asc" | "desc">("asc");

  function onSort(column: string) {
    if (sortBy === column) {
      setOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setOrder("asc");
    }
    setOffset(0);
  }

  const users = useAdminUsers({
    q: q || undefined,
    is_active: filterActive || undefined,
    is_superuser: filterSuper || undefined,
    has_2fa: filter2FA || undefined,
    sort_by: sortBy,
    order,
    limit: LIMIT,
    offset,
  });
  const selected = useAdminUserDetail(selectedId);

  const toggleActive = useToggleUserActive();
  const resetPassword = useResetUserPassword();
  const setSuperuser = useSetSuperuser();
  const markEmailVerified = useMarkEmailVerified();
  const anonymize = useAnonymizeUser();

  async function handleToggleActive(u: AdminUserDetail) {
    const disable = u.is_active;
    const ok = await confirm({
      title: disable ? t.disableTitle : t.enableTitle,
      message: disable
        ? fmt(t.disableMsg, { email: u.email })
        : fmt(t.enableMsg, { email: u.email }),
      variant: disable ? "danger" : "default",
      confirmLabel: disable ? t.disable : t.enable,
    });
    if (!ok) return;
    withStepUp(async (token) => {
      try {
        await toggleActive.mutateAsync({
          userId: u.id,
          enable: !u.is_active,
          stepUpToken: token,
        });
        toast.success(disable ? t.userDisabled : t.userEnabled);
      } catch (e) {
        toast.error(`${t.failed} ${formatErr(e)}`);
      }
    });
  }

  async function handleResetPassword(u: AdminUserDetail) {
    const ok = await confirm({
      title: t.resetTitle,
      message: fmt(t.resetMsg, { email: u.email }),
      variant: "danger",
      confirmLabel: t.resetConfirm,
    });
    if (!ok) return;
    withStepUp(async (token) => {
      try {
        const r = await resetPassword.mutateAsync({
          userId: u.id,
          stepUpToken: token,
        });
        // Auto-copy + one-shot reveal modal. Confirm-style modal is the
        // simplest way to surface the password with a "I saved it"
        // dismiss button without building a new prompt primitive.
        try {
          await navigator.clipboard.writeText(r.new_password);
        } catch {
          // clipboard write may fail (no permission, insecure context);
          // the password is still shown in the modal text below.
        }
        await confirm({
          title: t.newPwdTitle,
          message: fmt(t.newPwdMsg, { pwd: r.new_password, n: r.sessions_revoked }),
          confirmLabel: t.savedIt,
        });
        toast.success(
          fmt(t.pwdReset, { n: r.sessions_revoked }),
        );
      } catch (e) {
        toast.error(`${t.resetFailed} ${formatErr(e)}`);
      }
    });
  }

  async function handleToggleSuperuser(u: AdminUserDetail) {
    const grant = !u.is_superuser;
    const ok = await confirm({
      title: grant ? t.grantTitle : t.revokeTitle,
      message: grant
        ? fmt(t.grantMsg, { email: u.email })
        : fmt(t.revokeMsg, { email: u.email }),
      variant: "danger",
      confirmLabel: grant ? t.grantConfirm : t.revokeConfirm,
    });
    if (!ok) return;
    withStepUp(async (token) => {
      try {
        await setSuperuser.mutateAsync({
          userId: u.id,
          isSuperuser: grant,
          stepUpToken: token,
        });
        toast.success(t.superUpdated);
      } catch (e) {
        toast.error(`${t.updateFailed} ${formatErr(e)}`);
      }
    });
  }

  async function handleMarkEmailVerified(u: AdminUserDetail) {
    const ok = await confirm({
      title: t.markVerifiedTitle,
      message: fmt(t.markVerifiedMsg, { email: u.email }),
      variant: "danger",
      confirmLabel: t.markVerifiedConfirm,
    });
    if (!ok) return;
    withStepUp(async (token) => {
      try {
        await markEmailVerified.mutateAsync({
          userId: u.id,
          stepUpToken: token,
        });
        toast.success(t.emailMarkedVerified);
      } catch (e) {
        toast.error(`${t.markVerifiedFailed} ${formatErr(e)}`);
      }
    });
  }

  async function handleExport(u: AdminUserDetail) {
    try {
      const data = await downloadUserExport(u.id);
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `user_${u.id}_export.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t.exported);
    } catch (e) {
      toast.error(`${t.exportFailed} ${formatErr(e)}`);
    }
  }

  async function handleAnonymize(u: AdminUserDetail) {
    const ok = await confirm({
      title: t.anonTitle,
      message: fmt(t.anonMsg, { email: u.email, id: u.id }),
      variant: "danger",
      confirmLabel: t.anonConfirm,
    });
    if (!ok) return;
    withStepUp(async (token) => {
      try {
        await anonymize.mutateAsync({
          userId: u.id,
          reason: "",
          stepUpToken: token,
        });
        toast.success(t.userAnonymized);
        setSelectedId(null);
      } catch (e) {
        toast.error(`${t.anonFailed} ${formatErr(e)}`);
      }
    });
  }

  if (users.isError) {
    return (
      <Card className="border-danger">
        <div className="text-danger font-mono text-[11px]">
          {formatErr(users.error)}
        </div>
      </Card>
    );
  }

  const d = users.data;

  return (
    <div>
      {/* Filters */}
      <Card className="mb-3 !px-[14px] !py-3">
        <div className="flex gap-[10px] items-center flex-wrap">
          <input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setOffset(0);
            }}
            placeholder={t.searchPlaceholder}
            className="flex-[1_1_200px] min-w-[200px] px-[10px] py-[7px] bg-bg border border-stroke rounded text-content font-mono text-[11px] outline-none"
          />
          <select
            value={filterActive}
            onChange={(e) => {
              setFilterActive(e.target.value);
              setOffset(0);
            }}
            className="px-[10px] py-[7px] bg-bg border border-stroke rounded text-content font-mono text-[11px]"
          >
            <option value="">{t.allStatus}</option>
            <option value="true">{t.active}</option>
            <option value="false">{t.disabled}</option>
          </select>
          <select
            value={filterSuper}
            onChange={(e) => {
              setFilterSuper(e.target.value);
              setOffset(0);
            }}
            className="px-[10px] py-[7px] bg-bg border border-stroke rounded text-content font-mono text-[11px]"
          >
            <option value="">{t.allRoles}</option>
            <option value="true">{t.superusers}</option>
            <option value="false">{t.regular}</option>
          </select>
          <select
            value={filter2FA}
            onChange={(e) => {
              setFilter2FA(e.target.value);
              setOffset(0);
            }}
            className="px-[10px] py-[7px] bg-bg border border-stroke rounded text-content font-mono text-[11px]"
          >
            <option value="">{t.all2fa}</option>
            <option value="true">{t.twofaEnabledOpt}</option>
            <option value="false">{t.twofaDisabledOpt}</option>
          </select>
          <div className="flex items-center gap-3 ml-auto">
            {d && (
              <div className="font-mono text-[10px] text-muted">
                {fmt(t.usersCount, { n: d.total ?? 0 })}
              </div>
            )}
            <UsersCsvExportButton
              screen={{ q, is_active: filterActive, has_2fa: filter2FA }}
            />
          </div>
        </div>
      </Card>

      {/* Users list — desktop grid table from md+, stacked cards below.
          The 6-column grid (~460px min) horizontally scrolled on phones. */}
      {!d ? (
        <Loading text={t.loading} />
      ) : d.users.length === 0 ? (
        <Card>
          <div className="text-dim text-center py-6 font-mono text-[11px]">
            {t.noUsers}
          </div>
        </Card>
      ) : (
        <>
          {/* Mobile: stacked cards */}
          <div className="md:hidden flex flex-col gap-3">
            {d.users.map((u: AdminUserRow) => (
              <Card
                key={u.id}
                onClick={() => setSelectedId(u.id)}
                className="!p-4"
                style={{
                  background: u.anonymized_at ? "rgb(var(--color-bg2))" : undefined,
                  opacity: u.anonymized_at ? 0.6 : 1,
                }}
              >
                <div className="flex justify-between items-start gap-3 mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-sans text-[14px] font-semibold text-content truncate">
                      {u.full_name || "—"}
                    </div>
                    <div className="font-mono text-[11px] text-muted truncate">
                      {u.email}
                    </div>
                  </div>
                  {u.is_superuser && (
                    <span
                      className="px-2 py-[3px] rounded-[3px] text-[9px] font-bold uppercase tracking-[0.05em] shrink-0"
                      style={{
                        background: "rgb(var(--color-danger) / 0.133)",
                        border: "1px solid rgb(var(--color-danger) / 0.4)",
                        color: "rgb(var(--color-danger))",
                      }}
                    >
                      {t.super}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[10px]">
                  {u.anonymized_at ? (
                    <span className="text-dim">{t.statusAnonymized}</span>
                  ) : (
                    <span
                      style={{ color: u.is_active ? "rgb(var(--color-success))" : "rgb(var(--color-danger))" }}
                    >
                      {u.is_active ? t.statusActive : t.statusDisabled}
                    </span>
                  )}
                  <span
                    style={{ color: u.email_verified ? "rgb(var(--color-success))" : "rgb(var(--color-warning))" }}
                    title={
                      u.email_verified
                        ? t.emailConfirmed
                        : t.emailNotVerified
                    }
                  >
                    ✉ {u.email_verified ? t.verified : t.unverified}
                  </span>
                  <span
                    style={{ color: u.totp_enabled ? "rgb(var(--color-success))" : "rgb(var(--color-dim))" }}
                  >
                    2FA {u.totp_enabled ? t.twofaOn : t.twofaOff}
                  </span>
                  <span className="text-muted">
                    {t.lastLogin}{" "}
                    {u.last_login_at
                      ? new Date(u.last_login_at).toLocaleDateString()
                      : t.never}
                  </span>
                </div>
              </Card>
            ))}
          </div>

          {/* Desktop: grid table */}
          <Card className="!p-0 overflow-hidden hidden md:block">
            <div
              className="grid gap-3 px-4 py-3 bg-bg2 border-b border-stroke font-mono text-[10px] text-muted uppercase tracking-[0.05em] font-semibold items-center"
              style={{ gridTemplateColumns: GRID }}
            >
              <SortableHeader label={t.colUser} column="email" active={sortBy} order={order} onSort={onSort} />
              <SortableHeader label={t.colStatus} column="is_active" active={sortBy} order={order} onSort={onSort} />
              <div>{t.colRole}</div>
              <SortableHeader label={t.colVerified} column="email_verified" active={sortBy} order={order} onSort={onSort} />
              <div>{t.col2fa}</div>
              <SortableHeader label={t.colLastLogin} column="last_login" active={sortBy} order={order} onSort={onSort} />
              <div />
            </div>
            {d.users.map((u: AdminUserRow) => (
              <div
                key={u.id}
                onClick={() => setSelectedId(u.id)}
                className="grid gap-3 px-4 py-3 border-b border-stroke font-mono text-[11px] items-center cursor-pointer"
                style={{
                  gridTemplateColumns: GRID,
                  background: u.anonymized_at ? "rgb(var(--color-bg2))" : "transparent",
                  opacity: u.anonymized_at ? 0.6 : 1,
                }}
              >
                <div>
                  <div className="text-content font-semibold">
                    {u.full_name || "—"}
                  </div>
                  <div className="text-muted text-[10px] mt-[2px] truncate">
                    {u.email}
                  </div>
                </div>
                <div>
                  {u.anonymized_at ? (
                    <span className="text-dim text-[9px]">{t.anon}</span>
                  ) : (
                    <span
                      className="text-[10px]"
                      style={{ color: u.is_active ? "rgb(var(--color-success))" : "rgb(var(--color-danger))" }}
                    >
                      {u.is_active ? t.statusActive : t.statusDisabled}
                    </span>
                  )}
                </div>
                <div>
                  {u.is_superuser ? (
                    <span
                      className="px-[6px] py-[2px] rounded-[3px] text-[9px] font-bold uppercase"
                      style={{
                        background: "rgb(var(--color-danger) / 0.133)",
                        border: "1px solid rgb(var(--color-danger) / 0.4)",
                        color: "rgb(var(--color-danger))",
                      }}
                    >
                      {t.super}
                    </span>
                  ) : (
                    <span className="text-muted text-[10px]">—</span>
                  )}
                </div>
                <div>
                  <span
                    className="text-[10px]"
                    style={{ color: u.email_verified ? "rgb(var(--color-success))" : "rgb(var(--color-warning))" }}
                    title={
                      u.email_verified
                        ? t.emailConfirmedDesk
                        : t.emailNotVerifiedDesk
                    }
                  >
                    {u.email_verified ? t.yes : t.no}
                  </span>
                </div>
                <div>
                  <span
                    className="text-[10px]"
                    style={{ color: u.totp_enabled ? "rgb(var(--color-success))" : "rgb(var(--color-dim))" }}
                  >
                    {u.totp_enabled ? t.onLabel : t.offLabel}
                  </span>
                </div>
                <div className="text-muted text-[10px]">
                  {u.last_login_at
                    ? new Date(u.last_login_at).toLocaleDateString()
                    : t.never}
                </div>
                <div className="text-right">
                  <span className="text-accent text-[10px]">{t.detail}</span>
                </div>
              </div>
            ))}
          </Card>
        </>
      )}

      {/* Pagination */}
      {d && d.total > LIMIT && (
        <div className="flex justify-center gap-2 mt-3">
          <Button
            variant="default"
            small
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - LIMIT))}
          >
            {t.prev}
          </Button>
          <span className="font-mono text-[11px] text-muted self-center">
            {offset + 1}-{Math.min(offset + LIMIT, d.total)} {t.rangeOf} {d.total}
          </span>
          <Button
            variant="default"
            small
            disabled={offset + LIMIT >= d.total}
            onClick={() => setOffset(offset + LIMIT)}
          >
            {t.next}
          </Button>
        </div>
      )}

      {/* Detail drawer — full-width on mobile, fixed 480px panel from md+ */}
      {selectedId != null && selected.data && (
        <div
          className="fixed inset-0 md:top-0 md:right-0 md:bottom-0 md:left-auto md:inset-y-0 md:w-[480px] overflow-auto"
          style={{
            background: "rgb(var(--color-bg2))",
            borderLeft: "2px solid rgb(var(--color-accent))",
            zIndex: 900,
            boxShadow: "-4px 0 16px rgba(0,0,0,0.5)",
          }}
        >
          <div className="p-5 border-b border-stroke flex justify-between items-center">
            <div>
              <div className="font-display text-[16px] font-bold text-content">
                {selected.data.full_name || fmt(t.userNum, { id: selected.data.id })}
              </div>
              <div className="font-mono text-[11px] text-muted mt-[2px]">
                {selected.data.email}
              </div>
            </div>
            <button
              onClick={() => setSelectedId(null)}
              className="bg-transparent border-none text-muted text-2xl cursor-pointer px-2"
            >
              ×
            </button>
          </div>
          <div className="p-5">
            {/* Status grid */}
            <div className="grid grid-cols-2 gap-[10px] mb-4">
              <StatCell
                label={t.statusLabel}
                value={selected.data.is_active ? t.statusActive : t.statusDisabled}
                color={selected.data.is_active ? "rgb(var(--color-success))" : "rgb(var(--color-danger))"}
              />
              <StatCell
                label={t.superuser}
                value={selected.data.is_superuser ? t.yesShort : t.noShort}
                color={selected.data.is_superuser ? "rgb(var(--color-danger))" : "rgb(var(--color-muted))"}
              />
              <StatCell
                label={t.twofa}
                value={selected.data.totp_enabled ? t.enabledShort : t.disabledShort}
                color={selected.data.totp_enabled ? "rgb(var(--color-success))" : "rgb(var(--color-dim))"}
              />
              <StatCell
                label={t.authProvider}
                value={selected.data.auth_provider}
                color="rgb(var(--color-content))"
              />
              <StatCell
                label={t.activeSessions}
                value={String(selected.data.active_sessions ?? 0)}
                color="rgb(var(--color-content))"
              />
              <StatCell
                label={t.engagementsCreated}
                value={String(selected.data.engagements_created ?? 0)}
                color="rgb(var(--color-content))"
              />
            </div>

            {/* LGPD */}
            <div
              className="px-3 py-[10px] rounded mb-4"
              style={{ background: "rgb(var(--color-bg))", border: "1px solid rgb(var(--color-stroke))" }}
            >
              <div className="font-mono text-[9px] text-muted uppercase mb-[6px]">
                {t.lgpdData}
              </div>
              <div className="font-mono text-[10px] text-muted">
                {t.consent}{" "}
                {selected.data.consent_given_at ? (
                  <span className="text-success">
                    ✓{" "}
                    {new Date(selected.data.consent_given_at).toLocaleDateString()}{" "}
                    {fmt(t.consentVer, { v: selected.data.consent_version ?? "?" })}
                  </span>
                ) : (
                  <span className="text-warning">{t.preLgpd}</span>
                )}
              </div>
              {selected.data.anonymized_at && (
                <div className="font-mono text-[10px] text-danger mt-1">
                  {t.anonymizedAt}{" "}
                  {new Date(selected.data.anonymized_at).toLocaleString()}
                </div>
              )}
            </div>

            {/* Memberships */}
            {selected.data.memberships && selected.data.memberships.length > 0 && (
              <div className="mb-4">
                <div className="font-mono text-[9px] text-muted uppercase mb-[6px]">
                  {t.organizations}
                </div>
                {selected.data.memberships.map((m) => (
                  <div
                    key={m.membership_id}
                    className="flex justify-between px-[10px] py-[6px] rounded mb-1 font-mono text-[11px]"
                    style={{
                      background: "rgb(var(--color-bg))",
                      border: "1px solid rgb(var(--color-stroke))",
                    }}
                  >
                    <span className="text-content">{m.organization_name}</span>
                    <span className="text-accent uppercase text-[9px] font-bold">
                      {roleMap[m.role as keyof typeof roleMap] ?? m.role}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Actions — primary action stays loud (Disable/Enable, the
                most-used moderation lever); other admin operations
                collapse into a kebab so the drawer doesn't stack five
                same-weight buttons that compete for attention. */}
            {selected.data.anonymized_at ? (
              <div
                className="p-3 rounded text-dim font-mono text-[11px] text-center"
                style={{ background: "rgb(var(--color-bg))" }}
              >
                {t.anonNoActions}
              </div>
            ) : (
              <div>
                <div className="font-mono text-[9px] text-muted uppercase mb-2">
                  {t.actionsStepUp}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="default"
                    full
                    onClick={() => handleToggleActive(selected.data!)}
                  >
                    {selected.data.is_active ? t.disableUser : t.enableUser}
                  </Button>
                  <KebabMenu
                    ariaLabel={t.moreActions}
                    items={[
                      {
                        label: t.resetPassword,
                        onClick: () => handleResetPassword(selected.data!),
                        disabled: selected.data.auth_provider !== "local",
                      },
                      {
                        label: selected.data.is_superuser
                          ? t.revokeSuperuser
                          : t.grantSuperuser,
                        onClick: () => handleToggleSuperuser(selected.data!),
                        disabled: selected.data.id === currentUser?.id,
                      },
                      {
                        label: t.markEmailVerified,
                        onClick: () => handleMarkEmailVerified(selected.data!),
                        disabled: selected.data.email_verified,
                      },
                      {
                        label: t.exportData,
                        onClick: () => handleExport(selected.data!),
                      },
                      {
                        label: t.anonymizeErasure,
                        onClick: () => handleAnonymize(selected.data!),
                        variant: "danger",
                        disabled: selected.data.id === currentUser?.id,
                      },
                    ]}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCell({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color: string;
}) {
  return (
    <div
      className="p-[10px] rounded"
      style={{ background: "rgb(var(--color-bg))", border: "1px solid rgb(var(--color-stroke))" }}
    >
      <div className="font-mono text-[9px] text-dim uppercase mb-[3px]">
        {label}
      </div>
      <div
        className="font-mono text-[12px] font-semibold"
        style={{ color }}
      >
        {value}
      </div>
    </div>
  );
}
